import { lazy, Suspense, useEffect, useState } from 'react';
import { useSession } from '@/app/session';
import { keepMailOnDevice } from '@/offline/keepOnDevice';

// The card, the store reader and the composer it opens are a chunk of their
// own: nothing of them is on the path to the first paint (#208). Plain
// `React.lazy`, not `lazyWithPreload`: nothing prefetches this card, and that
// helper's module lives in a shared chunk the entry would then preload,
// stylesheet and all.
const DraftRecoveryCard = lazy(() =>
  import('@/routes/mail/DraftRecoveryCard').then((module) => ({ default: module.DraftRecoveryCard })),
);

/** How long after sign-in the local store is first looked at. */
export const RECOVERY_DELAY_MS = 1_500;

/**
 * Mounts the "Recover unsaved draft" card (#180) once the session is known,
 * a moment after the shell has painted, so reading IndexedDB never competes
 * with the first mail list.
 */
export function DraftRecovery() {
  const { account } = useSession();
  // Nothing can have been kept on disk without IndexedDB, or while the device
  // may not keep mail (ADR 0060): then the card's chunk is never fetched.
  const owner = typeof indexedDB !== 'undefined' && keepMailOnDevice() ? account?.id : undefined;
  const [armed, setArmed] = useState<string>();
  useEffect(() => {
    if (owner === undefined) return;
    const timer = setTimeout(() => setArmed(owner), RECOVERY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [owner]);
  if (owner === undefined || armed !== owner) return null;
  return (
    <Suspense fallback={null}>
      <DraftRecoveryCard owner={owner} />
    </Suspense>
  );
}
